/**
 * The song the composer just made.
 *
 * Reads as a music asset rather than as a form response: cover, then title, then
 * metadata, then playback, then secondary actions — in that order, at descending
 * visual weight. The prototype put the title at 13px with a download and a play
 * glyph beside it, the waveform under that, and then pasted the entire lyric sheet
 * into an always-open 220px scroll panel, which made the result the tallest thing
 * on the screen by a wide margin.
 *
 * Lyrics are still here and still complete; they are behind a disclosure, because
 * the first thing you want after generating a song is to hear it. Copy and download
 * of the lyrics moved into the More menu, which is where the drawer keeps them too.
 *
 * SIMULATED: the cover is a deterministic gradient, not generated art, and there is
 * no audio behind the player. See songs/ArtworkPlaceholder and studio/TrackPlayer.
 */
import { useState } from 'react';
import {
  ChevronDown,
  Copy,
  Download,
  FileText,
  Music,
  SlidersHorizontal,
  Trash2,
  X,
} from 'lucide-react';
import { ActionMenu, Btn } from '../ui';
import { useLiveDateLabel } from '../../hooks/useLiveDateLabel';
import { LyricSheet, SongArtwork } from '../songs';
import TrackPlayer from './TrackPlayer';
import { joinWithMiddleDot, parseLength } from '../../lib/formatting';
import { c } from '../../styles/theme';
import type { Song } from '../../types/domain';

export interface TrackResultProps {
  song: Song;
  playing: boolean;
  /** Seconds buffered from the start of the media, when known. */
  bufferedSec?: number;
  /** True when loaded but possibly paused — see TrackPlayer. Defaults to `playing`. */
  active?: boolean;
  elapsed: number;
  /** Playback rate, passed to the scrubber. Defaults to 1×. See lib/playback. */
  speed?: number;
  /** True while this song's real audio is loading/buffering. */
  loading?: boolean;
  onTogglePlay: () => void;
  onSeek?: (seconds: number) => void;
  /** Opens this song in the Studio drawer. */
  onOpenStudio: () => void;
  onDownload: () => void;
  onCopyLyrics: () => void;
  onDownloadLyrics: () => void;
  onDismiss?: () => void;
  onDelete?: () => void;
}

export default function TrackResult({
  song,
  playing,
  bufferedSec = 0,
  active = playing,
  elapsed,
  speed = 1,
  loading = false,
  onTogglePlay,
  onSeek,
  onOpenStudio,
  onDownload,
  onCopyLyrics,
  onDownloadLyrics,
  onDismiss,
  onDelete,
}: TrackResultProps) {
  const [showLyrics, setShowLyrics] = useState(false);
  const dateLabel = useLiveDateLabel(song.createdAt, song.date);
  const lyricPosition = active ? elapsed / parseLength(song.length) : null;
  const lyricElapsedSec = active ? elapsed : null;

  return (
    <section
      aria-label="Your new song"
      style={{
        marginTop: 24,
        background: c.surface1,
        border: `1px solid ${c.border}`,
        borderRadius: 14,
        padding: 18,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <SongArtwork
          song={song}
          height={64}
          radius={10}
          style={{ width: 64, flexShrink: 0, overflow: 'hidden' }}
        >
          <Music
            size={18}
            color="rgba(255,255,255,0.5)"
            style={{ position: 'absolute', bottom: 7, left: 8 }}
          />
        </SongArtwork>

        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              fontFamily: 'Space Grotesk',
              fontWeight: 600,
              fontSize: 15,
              margin: '0 0 3px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {song.title}
          </p>
          <p style={{ fontSize: 11, color: c.text2, margin: 0 }}>
            {joinWithMiddleDot([song.genre, song.length, dateLabel])}
          </p>
        </div>

        <ActionMenu
          label={`More actions for ${song.title}`}
          items={[
            {
              label: 'Open in Studio',
              icon: <SlidersHorizontal size={13} color={c.text2} />,
              onSelect: onOpenStudio,
            },
            {
              label: 'Download',
              icon: <Download size={13} color={c.text2} />,
              onSelect: onDownload,
            },
            {
              label: 'Copy lyrics',
              icon: <Copy size={13} color={c.text2} />,
              onSelect: onCopyLyrics,
            },
            {
              label: 'Download lyrics',
              icon: <FileText size={13} color={c.text2} />,
              onSelect: onDownloadLyrics,
            },
            ...(onDismiss
              ? [
                  {
                    label: 'Save and Dismiss',
                    icon: <X size={13} color={c.text2} />,
                    onSelect: onDismiss,
                  },
                ]
              : []),
            ...(onDelete
              ? [
                  {
                    label: 'Delete',
                    icon: <Trash2 size={13} color={c.danger} />,
                    onSelect: onDelete,
                    danger: true,
                  },
                ]
              : []),
          ]}
        />
      </div>

      <div style={{ marginTop: 16 }}>
        <TrackPlayer
          song={song}
          playing={playing}
          active={active}
          elapsed={elapsed}
          bufferedSec={bufferedSec}
          speed={speed}
          loading={loading}
          onTogglePlay={onTogglePlay}
          onSeek={onSeek}
          bars={28}
        />
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginTop: 16,
          flexWrap: 'wrap',
        }}
      >
        {/* The one thing to do next with a finished song, and the only button here:
            everything else on this card is in the menu above. */}
        <Btn variant="secondary" onClick={onOpenStudio}>
          <SlidersHorizontal size={13} />
          Open in Studio
        </Btn>

        {song.lyrics && (
          <button
            type="button"
            onClick={() => setShowLyrics((v) => !v)}
            aria-expanded={showLyrics}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontSize: 12,
              fontFamily: 'Inter',
              color: c.text2,
            }}
          >
            Lyrics
            <ChevronDown
              size={13}
              color={c.text2}
              style={{
                transform: showLyrics ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
            />
          </button>
        )}
      </div>

      {song.lyrics && showLyrics && (
        <div
          style={{
            marginTop: 14,
            borderTop: `1px solid ${c.border}`,
            paddingTop: 14,
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          <LyricSheet
            lyrics={song.lyrics}
            at={lyricPosition}
            elapsedSec={lyricElapsedSec}
            lyricTimings={song.lyricTimings}
            lyricSectionTimings={song.lyricSectionTimings}
          />
        </div>
      )}
    </section>
  );
}
